<template>
  <div class="order-steps-container">
    <arch
      @go-to-step="goToStep"
      :titles="steps"
      :activeStep="activeStep"
      :errors="errors"
      @submit-order="sendOrder"
      :uploading="isUploading"
    ></arch>
    <section>
      <router-view :secure="printOrderStore.orderDetails.secure"></router-view>
    </section>
  </div>
</template>
<script>
import Arch from '@/components/Arch.vue'
import isEmpty from 'lodash-es/isEmpty'
import SendingOrder from '@/dialogs/SendingOrder.vue'
import OrderConfirm from '@/dialogs/OrderConfirm.vue'
import OrderRedirectConfirm from '@/dialogs/OrderRedirectConfirm.vue'

import ErrorMessage from '@/dialogs/ErrorMessage.vue'
import OrderMixin from '@/mixins/OrderMixin'
import { EventBus } from '@/eventbus/EventBus'
import { saveOrder } from '@/api/api'
import { useShopsStore } from '@/stores/Shops.store'
import { usePrintOrderStore } from '@/features/printOrder/PrintOrder.store'
import { useFileListStore } from '@/stores/FileList.store'
import { mapStores } from 'pinia'
import { usePrintStoreStore } from '@/features/printstore/PrintStore.store'
import { useProductOrderStore } from '@/features/productOrder/ProductOrder.store'

export default {
  props: {
    secure: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      step: 'step1',
      steps: ['Printen', 'Bezorgen', 'Factureren', 'Controleren'],
      dates: [],
      inProgress: false,
      printOrderV2: false
    }
  },
  mixins: [OrderMixin],
  components: {
    Arch
  },
  methods: {
    goToStep(step) {
      switch (step) {
        case 'step1':
          this.$router.push({
            name: 'printOrder',
            query: { secure: this.printOrderStore.orderDetails.secure }
          })
          break

        case 'step2':
          this.$router.push({
            name: 'printOrderDeliver'
          })
          break
        case 'step3':
          this.$router.push({
            name: 'printOrderInvoice'
          })
          break
        case 'step4':
          this.$router.push({
            name: 'printOrderConfirm'
          })
          break
      }
    },
    updateOrder(key, val) {
      this.printOrderStore.updateOrderDetails(key, val)
    },
    fileHasRequiredSpecsMissing(file) {
      const specs = file.productSpecs?.specs
      if (!specs?.length) return false
      const product = this.productOrderStore.products.find(
        (p) => p.title === file.productSpecs?.title
      )
      if (!product) return false
      for (const menu of product.menus) {
        for (const optionGroup of menu.optionGroups || []) {
          if (!optionGroup.required) continue
          const spec = specs.find((s) => s.optionGroupName === optionGroup.name)
          if (!spec || !spec.optionTitle) return true
        }
      }
      return false
    },
    async submitDummyOrder() {
      // Dummy order: geen echte saveOrder, maar wél de geuploade files
      // server-side verwijderen zodat ze niet terugkomen na een refresh.
      const fileIds = this.fileListStore.fileList.map((f) => f.uuid)
      if (fileIds.length > 0) {
        try {
          await this.fileListStore.removeFiles(fileIds)
        } catch (error) {
          console.log(error)
        }
      }
      return { dummy: true }
    },
    sendOrder() {
      const missingFile = this.fileListStore.fileList.find((f) =>
        this.fileHasRequiredSpecsMissing(f)
      )
      if (missingFile) {
        this.printOrderStore.addOrderError('required-specs')
        return
      }
      this.printOrderStore.removeOrderError('required-specs')

      if (this.inProgress) return
      this.inProgress = true
      EventBus.emit('orderSubmitting')

      this.$modal.show(SendingOrder, null, {
        height: 120,
        width: 280,
        adaptive: true,
        name: 'sending',
        clickToClose: false
      })

      const order = this.prepareOrder(this.printOrderStore.orderDetails)

      if (!order) {
        this.$modal.hide('sending')
        this.inProgress = false
        return
      }

      if (this.fileListStore?.fileList?.length > 0) {
        //add files to order
        order.cart = [...this.fileListStore.fileList]

        order.cart.forEach((file) => {
          if (this.rootStore.theme.customFields.printOrder?.cartV2) {
            file = this.prepareProductFile(file)
          } else {
            file = this.prepareLegacyPrintFile(file)
          }

          /* else if (file.productSpecs && file.productSpecs.title) {
            file.productSpecs.Product = file.productSpecs.title
            delete file.productSpecs.title
            file.printType = 'PRINT_TEMPLATE'
          } else {
            file.printType = 'PRINT_CUSTOM'
          } */
        })
        //hide specs if needed
        if (this.customFields.printSpecs && this.customFields.printSpecs.hide) {
          order.cart.forEach((element) => {
            element.productSpecs = {}
            element.format = ''
            element.material = ''
            element.colorMode = ''
            element.side = ''
            element.treatment = ''
            element.productSpecs.title = 'Zie instructies'
          })
        }
      }

      if (this.printStoreStore.orderDetails.cart.length > 0) {
        const products = this.preparePrintStoreCart(
          this.printStoreStore.orderDetails.cart
        )

        order.cart = [...order.cart, ...products]
      }
      //console.log(order)
      //return

      const isDummy = this.rootStore.theme?.customFields?.checkOut?.dummyOrder

      const orderRequest = isDummy
        ? this.submitDummyOrder()
        : saveOrder(order)

      orderRequest
        .then((response) => {
          this.inProgress = false
          this.$modal.hideAll()

          this.printStoreStore.emptyCart()
          this.fileListStore.removeAllFiles()
          this.printOrderStore.resetOrderDetails()

          const orderRedirect =
            this.rootStore.theme.customFields.orderRedirect

          if (orderRedirect && response.redirectUrl) {
            this.$modal.show(
              OrderRedirectConfirm,
              {
                url: response.redirectUrl
              },
              {
                width: 360,
                height: 'auto',
                clickToClose: false
              }
            )
          } else {
            this.$modal.show(
              OrderConfirm,
              {},
              {
                width: 360,
                height: 'auto',
                clickToClose: false
              },
              {
                'before-close': () => {
                  this.step = 'step1'
                }
              }
            )
          }
        })
        .catch((error) => {
          this.inProgress = false
          EventBus.emit('orderFailed')
          this.$modal.hide('sending')
          console.log(error)
          if ('UPLOAD_ERROR' === error.data.error) {
            this.$modal.show(
              ErrorMessage,
              {
                title: 'Fout bij het plaatsen van de order',
                text:
                  'Uw bestanden zijn niet correct geupload. Uw winkelwagen is geleegd. Probeer de bestanden opnieuw te uploaden.'
              },
              {
                height: 250,
                width: 380,
                adaptive: true,
                clickToClose: false
              }
            )
            this.fileListStore.removeAllFiles()
          } else if ('FILES_MISSING_FROM_SESSION' === error.data.error) {
            const missingFiles = error.data.files || []
            const meerdere = missingFiles.length > 1
            const fileNamesHtml = missingFiles
              .map((f) => `<strong>${f}</strong>`)
              .join('<br>')
            const text = meerdere
              ? `De bestanden<br>${fileNamesHtml}<br>zijn niet correct geupload, de bestanden zijn uit uw upload verwijderd. Upload ze opnieuw om uw order af te kunnen ronden.`
              : `Het bestand<br>${fileNamesHtml}<br>is niet correct geupload, het bestand is uit uw upload verwijderd. Upload het opnieuw om uw order af te kunnen ronden.`
            this.$modal.show(
              ErrorMessage,
              {
                title: 'Fout bij het plaatsen van de order',
                text
              },
              {
                height: 250,
                width: 380,
                adaptive: true,
                clickToClose: false
              }
            )
            for (const fileName of missingFiles) {
              const file = this.fileListStore.fileList.find(
                (f) => f.fileName === fileName
              )
              if (file) {
                this.fileListStore.removeFile(file.uuid)
              }
            }
          } else {
            this.$modal.show(
              ErrorMessage,
              {
                title: 'Fout bij het plaatsen van de order',
                text: 'Neem contact op met de Rijnja Helpdesk'
              },
              {
                height: 250,
                width: 380,
                adaptive: true,
                clickToClose: false
              }
            )
          }
        })
    }
  },
  computed: {
    ...mapStores(
      usePrintOrderStore,
      useShopsStore,
      useFileListStore,
      usePrintStoreStore,
      useProductOrderStore
    ),
    customFields() {
      return this.rootStore.theme.customFields
    },
    shops() {
      return this.shopsStore.shopsList
    },
    orderDetails() {
      return this.printOrderStore.orderDetails
    },
    shop() {
      return this.printOrderStore.shop
    },
    errors() {
      return this.printOrderStore.orderErrors.length
    },
    isUploading() {
      return this.fileListStore.isUploading
    },
    activeStep() {
      if (
        this.$route.name === 'printOrder' ||
        this.$route.name == 'printOrderCartV2'
      ) {
        return 'step1'
      } else if (this.$route.name === 'printOrderDeliver') {
        return 'step2'
      } else if (this.$route.name === 'printOrderInvoice') {
        return 'step3'
      } else if (this.$route.name === 'printOrderConfirm') {
        return 'step4'
      }
    },
    customerNumber() {
      return this.printOrderStore.orderDetails.invoiceAddress.newCustomer
        ? 0
        : this.printOrderStore.orderDetails.invoiceAddress.customerNumber || 0
    }
  },
  created() {
    EventBus.on('submitOrder', this.sendOrder)

    /*     this.fileListStore.fileList.forEach((file) => {
      if (file.productSpecs?.specs?.length > 0) {
        this.fileListStore.addProductOrderProduct([
          [file.fileName],
          {
            title: '',
            specs: []
          }
        ])
      }
    }) */

    if (isEmpty(this.printOrderStore.orderDetails.deliveryAddress)) {
      const deliveryAddress = this.setDeliveryAddress()

      this.printOrderStore.updateOrderDetails(
        'deliveryAddress',
        deliveryAddress
      )
    }
    if (!this.orderDetails.invoiceAddress.customerNumber) {
      const invoiceDetails = this.getDefaultInvoiceDetails()

      this.printOrderStore.updateOrderDetails(
        'invoiceAddress',
        invoiceDetails.address
      )

      this.printOrderStore.updateProject(invoiceDetails.project)

      this.printOrderStore.updateOrderDetails(
        'reference',
        invoiceDetails.reference
      )
    }
    this.printOrderStore.updateOrderDetails('secure', this.secure)
    this.printOrderStore.getTheShop({
      deliveryAddress: this.printOrderStore.orderDetails.deliveryAddress,
      invoiceId: this.customerNumber,
      secure: this.printOrderStore.orderDetails.secure
    })
  },
  unmounted() {
    EventBus.off('submitOrder', this.sendOrder)
  },
  watch: {
    'orderDetails.deliveryAddress': function () {
      this.printOrderStore.getTheShop({
        deliveryAddress: this.printOrderStore.orderDetails.deliveryAddress,
        invoiceId: this.customerNumber,
        secure: this.printOrderStore.orderDetails.secure
      })
    },
    'orderDetails.invoiceAddress': function (val) {
      this.printOrderStore.getTheShop({
        deliveryAddress: this.printOrderStore.orderDetails.deliveryAddress,
        invoiceId: this.customerNumber,
        secure: this.printOrderStore.orderDetails.secure
      })
    },
    'fileListStore.fileList': {
      deep: true,
      handler() {
        const hasMissing = this.fileListStore.fileList.some((f) =>
          this.fileHasRequiredSpecsMissing(f)
        )
        if (hasMissing) {
          this.printOrderStore.addOrderError('required-specs')
        } else {
          this.printOrderStore.removeOrderError('required-specs')
        }
      }
    },
    shop: function (val) {
      this.printOrderStore
        .getTheDates({
          shopId: this.shop.id,
          invoiceId: this.customerNumber,
          secure: this.printOrderStore.orderDetails.secure
        })
        .then((dates) => {
          const storedDate = this.printOrderStore.orderDetails.deliveryDate

          // Validate stored date: reset if empty, spoed, or no longer active
          const needsNewDate =
            isEmpty(storedDate) ||
            (storedDate.title !== 'spoed' &&
              !dates.some(
                (date) => date.active && date.value === storedDate.value
              ))

          if (needsNewDate) {
            let delDate = dates.find((date) => date.active)
            if (delDate) {
              delDate.time = delDate.times[0].time
              this.printOrderStore.updateOrderDetails('deliveryDate', delDate)
            }
          }
        })
    }
  }
}
</script>
<style lang="scss"></style>
